import Head from 'next/head';
import React from 'react';
import { StatistikPortal_ } from '../../pagepart/statistik/StatistikPortal_';
import { calculateAverageGrade, calculateGradeInner, numericGradeToString } from '../../classes/service/grade/grade_utils';
import { memoize } from '../../utils/mobx/decorators/memoize';
import { Env } from '../../env/Env';
import { mysql } from '../../server';
import { getSecure, ServerProps } from '../../pagepart/portal/utils';
import { Store } from '../../pagepart/store/Store';
import { str_replace } from '../../utils/helper/helper_string';

export async function getServerSideProps(rawContext) {
  const { userEnv, portalEnv, env } = await Env.forNextContext(rawContext);

  const { user } = userEnv;
  const { mysqlStarttime, mysqlEndtime } = portalEnv;

  const sql = `Select t.path, t.pathname, l.topic_id, t.path as pathurl, l.knowledge, l.average, l.correct, l.questions, t.name, l.timestamp, l.seconds, l.points, l.maxPoints, l.avgPoints, t.webClientVersion, t.json_options from lesson l, topic t
    WHERE l.user_id = '${user.id}' AND l.topic_id = t.id AND l.questions > 0
    AND l.timestamp >= '${mysqlStarttime}' AND l.timestamp <= '${mysqlEndtime}'
        ORDER by l.timestamp desc`;
  // db.queryRaw
  const lessons = await mysql.mysqlForUserId(user.id).sql2Array<{
    topic_id: number;
    path: string;
    pathname: string;
    pathurl: string;
    correct: number;
    questions: number;
    name: string;
    timestamp: string;
    seconds: number;
    knowledge: number;
    average: number;
    points: number;
    maxPoints: number;
    avgPoints: number;
    webClientVersion: number;
    json_options: string;
    grade: number;
    averageGrade: number;
  }>(sql);

  lessons.forEach(lesson => {
    const json_options = JSON.parse(lesson.json_options);

    lesson.grade = calculateGradeInner(
      lesson.knowledge / 100,
      (lesson.knowledge - lesson.average) / 100,
      json_options.relativeknowledge_distribution,
    );

    lesson.averageGrade = calculateAverageGrade(lesson.average / 100);
  });

  return {
    props: {
      ...env.transfer,
      lessons,
      chartSecure: getSecure(user.id),
    },
  };
}

class App extends React.Component<ServerProps<typeof getServerSideProps>> {
  @memoize
  get store() {
    return Store.forApp(this);
  }

  setupPage(store: Store) {
    store.portalStore.metaTitle = 'Schlaukopf Statistikbereich';
    store.portalStore.includeStylesheetOnce('/static/dist7/assets/plugins/custom/fullcalendar/fullcalendar.bundle.css');
    store.portalStore.subheaderTitle = `Lernstatistiken von ${store.statistikStore.displayName}`;
  }

  render() {
    const { lessons, chartSecure } = this.props;

    return (
      <StatistikPortal_ store={this.store}>
        <div className="row">
          <Head>
            <script type="text/javascript" src="/static/dist/assets/plugins/custom/fullcalendar/fullcalendar.bundle.js"></script>
          </Head>
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <div className="timeline timeline-justified timeline-4">
                  <div className="timeline-bar" />
                  <div className="timeline-items">
                    {lessons.map((lesson, index) => (
                      <div className="timeline-item">
                        <div className="timeline-badge">
                          <div className="bg-danger" />
                        </div>
                        <div className="timeline-label">
                          <span className="text-primary font-weight-bold">
                            {' '}
                            <span style={{ marginRight: 10 }}>
                              {typeof window !== 'undefined' && window.moment(lesson.timestamp).utc().format('DD.MM.YYYY')}
                            </span>
                            <small>
                              {typeof window !== 'undefined' &&
                                window.moment(lesson.timestamp).subtract(lesson.seconds, 'seconds').utc().format('HH:mm')}{' '}
                              Uhr bis&nbsp;
                              {typeof window !== 'undefined' && window.moment(lesson.timestamp).utc().format('HH:mm')} Uhr
                            </small>
                          </span>
                        </div>
                        <div className="timeline-content">
                          <h3>
                            {lesson.name}{' '}
                            <small>
                              {str_replace(
                                '/',
                                ' > ',
                                str_replace('Abfrager.de/', '', str_replace(`/${lesson.name}`, '', lesson.pathname)),
                              )}
                            </small>
                          </h3>

                          <div>
                            <img
                              width="700"
                              height="360"
                              src={`/statistics/development_module.php?height=360&width=700&userid=${
                                this.store.userStore.user.id
                              }&use=protocol&title=${encodeURIComponent(lesson.name)}&moduleid=${
                                lesson.topic_id
                              }&secure=${encodeURIComponent(chartSecure)}`}
                            />
                          </div>

                          <div>
                            <span
                              className="kt-badge kt-badge--unified-success kt-badge--lg kt-badge--rounded kt-badge--bold"
                              style={{ width: 'auto', paddingLeft: 10, paddingRight: 10 }}
                            >
                              {lesson.correct} von {lesson.questions} Fragen richtig
                            </span>

                            <span
                              className="kt-section__content kt-section__content--solid"
                              style={{ width: 'auto', paddingLeft: 10, paddingRight: 10, marginLeft: 20 }}
                            >
                              Note:
                            </span>
                            <span
                              className={`kt-badge kt-badge--unified-${
                                lesson.appGrade < 3 ? 'success' : lesson.appGrade < 4 ? 'warning' : 'danger'
                              } kt-badge--lg kt-badge--rounded kt-badge--bold`}
                              style={{ width: 'auto', paddingLeft: 10, paddingRight: 10, marginLeft: 20 }}
                              data-toggle="kt-tooltip"
                              data-placement="right"
                              title=""
                              data-original-title={`Ø Durchschnittsnote anderer Nutzer in diesem Thema: ${numericGradeToString(
                                lesson.averageGrade,
                              )}`}
                            >
                              {numericGradeToString(lesson.appGrade)}
                            </span>
                          </div>
                          <div style={{ marginTop: 20 }}>
                            <a href={getUrlForLessonData(lesson)} target="_blank" className="btn btn-info">
                              Lernthema aufrufen
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </StatistikPortal_>
    );
  }
}

function getUrlForLessonData(lessonData) {
  let url = lessonData.pathurl;
  url = str_replace('abfrager.de', '', url);
  if (lessonData.webClientVersion === 0) {
    url += '.htm';
  }
  return url;
}

export default App;
